import { FocusDTO } from "js/api/focus";
import create from "zustand";

interface State {
	focusLibrary: FocusDTO[];
	fetchingLibraryItems: boolean;
	/**
	 * Whether or not the back button should be shown when creating/editing a focus from the Classroom page.
	 */
	openedDirectlyToEdit: boolean;
}

interface Actions {
	setFocusLibraryItems: (focusLibraryItems: FocusDTO[]) => void;
	setFetchingLibraryItems: (fetchingLibraryItems: boolean) => void;
	setOpenedDirectlyToEdit: (openedDirectlyToEdit: boolean) => void;
	getNumberOfFocusesInLibrary: () => number;
	getLibraryItems: () => FocusDTO[];
	/**
	 * Adds a focus to the library. If the focus already exists (based on ID), it will be updated.
	 */
	upsertFocusItemInLibrary: (focusItem: FocusDTO) => void;
	removeFocusItemFromLibrary: (focusId: string) => void;
	getLibraryItemById: (focusId: string) => FocusDTO | undefined;
}

export const useFocusStore = create<State & Actions>((set, get) => ({
	focusLibrary: [],
	fetchingLibraryItems: true,
	openedDirectlyToEdit: false,

	setOpenedDirectlyToEdit: (openedDirectlyToEdit: boolean) => set(() => ({ openedDirectlyToEdit })),
	setFocusLibraryItems: (focusLibraryItems: FocusDTO[]) => {
		set(() => ({
			focusLibrary: focusLibraryItems,
		}));
	},
	setFetchingLibraryItems: (fetchingLibraryItems: boolean) => set(() => ({ fetchingLibraryItems })),
	getNumberOfFocusesInLibrary: () => get().focusLibrary.length,
	getLibraryItems: () => get().focusLibrary,
	upsertFocusItemInLibrary: (focusItem: FocusDTO) => {
		if (!focusItem.id) {
			throw new Error("Focus item must have an ID to be added to the library.");
		}

		if (!focusItem.name || focusItem.name === "") {
			throw new Error("Focus item must have a name to be added to the library.");
		}

		const focusLibrary = get().focusLibrary;
		const existingFocusIndex = focusLibrary.findIndex((f) => f.id === focusItem.id);

		if (existingFocusIndex !== -1) {
			focusLibrary[existingFocusIndex] = focusItem;
		} else {
			focusLibrary.push(focusItem);
		}

		set(() => ({
			focusLibrary,
		}));
	},
	removeFocusItemFromLibrary: (focusId: string) => {
		const focusLibrary = get().focusLibrary;
		const existingFocusIndex = focusLibrary.findIndex((f) => f.id === focusId);

		if (existingFocusIndex !== -1) {
			focusLibrary.splice(existingFocusIndex, 1);
		}

		set(() => ({
			focusLibrary,
		}));
	},
	getLibraryItemById: (focusId: string) => get().focusLibrary.find((f) => f.id === focusId),
}));
